import { createSlice, createSelector } from '@reduxjs/toolkit'

const initialState = {
  desiredDates: {},
  pickupPoints: [],
  deliveryCosts: {},
  deliveryTypes: [],
  nonDeliverableItems: [],
  paymentTypes: {},
  paymentTypeDescriptions: {},
  personalData: {
    loading: false,
    data: null,
  },
  deliveryData: {
    loading: false,
    data: null,
  },
  paymentData: {
    loading: false,
    data: null,
  },
  lastOrder: {
    data: null,
    loading: false,
    error: null,
  },
  redirectPaymentData: {
    data: null,
    loading: false,
    error: null,
  },
  errorPaymentInfo: {
    data: null,
    loading: false,
    error: null,
  },
  timeSlots: {
    data: [],
    loading: false,
    error: null,
    onlySendOrder: false,
  },
  service: '',
  maxItems: 15,
  error: null,
  onlySendOrder: false,
  onlySelfPickup: false,
  productsFromMinsk: false,
}

export const PARAMS = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  PHONE: 'phone',
  EMAIL: 'email',
  COMMENT: 'order_comments',
  AGREEMENT: 'agreement',
  DELIVERY_TYPE: 'delivery_type',
  DELIVERY_TYPE_PICKUP: 'pickup',
  DELIVERY_TYPE_COURIER: 'local_delivery',
  DELIVERY_TYPE_BELARUS: 'delivery_by_country',
  SHOP_ID: 'shop_id',
  ADDRESS: 'address',
  DELIVERY_DATE: 'delivery_date',
  DELIVERY_TIME: 'delivery_time',
  PAYMENT_TYPE: 'payment_type',
  PAYMENT_TYPE_CASH: 'cash',
  PAYMENT_TYPE_CARD: 'courier_terminal',
  PAYMENT_TYPE_CARD_ONLINE: 'online',
  PAYMENT_TYPE_CERTIFICATE: 'certificate',
}

export const PAYMENT_TYPES = {
  [PARAMS.PAYMENT_TYPE_CASH]: 'Наличными',
  [PARAMS.PAYMENT_TYPE_CARD]: 'Банковской картой',
  [PARAMS.PAYMENT_TYPE_CARD_ONLINE]: 'Банковской картой онлайн',
}

export const DELIVERY_TYPES = {
  [PARAMS.DELIVERY_TYPE_PICKUP]: 'Самовывоз г. Минск',
  [PARAMS.DELIVERY_TYPE_COURIER]: 'Доставка курьером по г. Минску',
  [PARAMS.DELIVERY_TYPE_BELARUS]: 'Доставка по Беларуси',
}

export const DEFAULTS = {
  DELIVERY_TYPE: PARAMS.DELIVERY_TYPE_PICKUP,
  PAYMENT_TYPE: PARAMS.PAYMENT_TYPE_CASH,
  PAYMENT_TYPE_DELIVERY_BELARUS: PARAMS.PAYMENT_TYPE_CARD_ONLINE,
}

const checkout = createSlice({
  name: 'checkout',
  initialState: initialState,
  reducers: {
    requestPersonalDataValidation(state) {
      state.personalData.loading = true
    },
    successPersonalDataValidation(state, { payload }) {
      const { params, nextStepData } = payload

      state.personalData.loading = false
      state.error = null
      state.personalData.data = params
      state.desiredDates = nextStepData?.desired_dates ?? {}
      state.pickupPoints = nextStepData?.shops ?? []
      state.deliveryCosts = nextStepData?.delivery_costs ?? {}
      state.deliveryTypes = nextStepData?.delivery_types ?? []
    },
    successGetShops(state, { payload }) {
      state.pickupPoints = payload?.shops ?? [];
      state.onlySelfPickup = payload?.onlySelfPickup ?? false;
      state.productsFromMinsk = payload?.productsFromMinsk ?? false;
    },
    initGetTimeslots(state) {
      state.timeSlots.loading = true
      state.timeSlots.error = null
    },
    successGetTimeslots(state, { payload }) {
      state.timeSlots.data = payload?.desired_dates ?? []
      state.service = payload?.service ?? ''
      state.paymentTypes = payload?.payment_types ?? {}
      state.nonDeliverableItems = payload?.non_deliverable_items ?? []
      state.paymentTypeDescriptions = payload?.payment_type_descriptions ?? {}
      state.maxItems = payload?.maxItems ?? 15
      state.timeSlots.loading = false
      state.timeSlots.onlySendOrder = payload?.onlySendOrder ?? false;
      state.timeSlots.error = null;
      state.onlySendOrder = payload?.onlySendOrder ?? false;
    },
    errorGetTimeslots(state, { payload }) {
      state.timeSlots.loading = false;
      state.timeSlots.error = payload;
    },
    failurePersonalDataValidation(state, { payload }) {
      state.personalData.loading = false
      state.error = payload
    },
    requestDeliveryDataValidation(state) {
      state.deliveryData.loading = true
    },
    successDeliveryDataValidation(state, { payload }) {
      const { params, nextStepData } = payload

      state.deliveryData.loading = false
      state.error = null
      state.deliveryData.data = params
      state.paymentTypes = nextStepData?.payment_types ?? {}
      state.paymentTypeDescriptions = nextStepData?.payment_type_descriptions ?? {}
    },
    failureDeliveryDataValidation(state, { payload }) {
      state.deliveryData.loading = false
      state.error = payload

      if (payload?.desired_dates) {
        state.desiredDates = payload.desired_dates
      }
    },
    requestPaymentDataValidation(state) {
      state.paymentData.loading = true
    },
    successPaymentDataValidation(state, { payload }) {
      state.paymentData.loading = false
      state.error = null
      state.paymentData.data = payload
    },
    failurePaymentDataValidation(state, { payload }) {
      state.paymentData.loading = false
      state.error = payload
    },
    successOrderCreate(state) {
      state.lastOrder.data = null
    },
    resetError(state) {
      state.error = null
    },
    resetCheckoutData(state) {
      state.desiredDates = {}
      state.pickupPoints = []
      state.deliveryCosts = {}
      state.deliveryTypes = []
      state.paymentTypes = {}
      state.paymentTypeDescriptions = {}
      state.personalData.data = null
      state.deliveryData.data = null
      state.paymentData.data = null
      state.lastOrder.data = null
    },
    requestLastOrder(state) {
      state.lastOrder.loading = true
    },
    successLastOrder(state, { payload }) {
      if (payload) {
        state.lastOrder.data = payload
      }

      state.lastOrder.loading = false
      state.lastOrder.error = null
    },
    failureLastOrder(state, { payload }) {
      state.lastOrder.loading = false
      state.lastOrder.error = payload
    },
    requestRedirectPaymentData(state) {
      state.redirectPaymentData.loading = true
    },
    successRedirectPaymentData(state, { payload }) {
      if (payload) {
        state.redirectPaymentData.data = payload
      }

      state.redirectPaymentData.loading = false
      state.redirectPaymentData.error = null
    },
    failureRedirectPaymentData(state, { payload }) {
      state.redirectPaymentData.loading = false
      state.redirectPaymentData.error = payload
    },
    requestErrorPaymentInfo(state) {
      state.errorPaymentInfo.loading = true
    },
    successErrorPaymentInfo(state, { payload }) {
      if (payload) {
        state.errorPaymentInfo.data = payload
      }

      state.errorPaymentInfo.loading = false
      state.errorPaymentInfo.error = null
    },
    failureErrorPaymentInfo(state, { payload }) {
      state.errorPaymentInfo.loading = false
      state.errorPaymentInfo.error = payload
    },
  },
})

export const fetchLastOrder = ({ refresh = false, ...params } = {}) => async (dispatch, getState, { api }) => {
  try {
    const lastOrderData = getState().checkout.lastOrder?.data ?? null
    if (!refresh && lastOrderData && lastOrderData?.id == params?.order_id) {
      dispatch(successLastOrder())
      return
    } else {
      dispatch(requestLastOrder())
      const response = await api.getLastOrder(params)
      const { result, order } = response
      if (result === 'success') {
        dispatch(successLastOrder(order))
      } else {
        dispatch(failureLastOrder(response))
      }
    }
  } catch (err) {
    dispatch(failureLastOrder(err.toString()))
  }
}

export const fetchRedirectInfo = params => async (dispatch, getState, { api }) => {
  try {
    const redirectPaymentData = getState().checkout.redirectPaymentData?.data ?? null
    if (redirectPaymentData && redirectPaymentData?.order?.id == params?.order_id) {
      dispatch(successRedirectPaymentData())
      return
    } else {
      dispatch(requestRedirectPaymentData())
      const response = await api.getRedirectInfo(params)

      const { result, order, payment } = response

      if (result === 'success') {
        dispatch(successRedirectPaymentData({ order, payment }))
      } else {
        dispatch(failureRedirectPaymentData(response))
      }
    }
  } catch (err) {
    dispatch(failureRedirectPaymentData(err.toString()))
  }
}

export const fetchPaymentErrorInfo = ({ refresh = false, ...params } = {}) => async (dispatch, getState, { api }) => {
  try {
    const errorPaymentInfo = getState().checkout.errorPaymentInfo?.data ?? null
    if (!refresh && errorPaymentInfo && errorPaymentInfo?.payment?.id == params?.payment_id) {
      dispatch(successErrorPaymentInfo())
      return
    } else {
      dispatch(requestErrorPaymentInfo())
      const response = await api.getErrorPaymentInfo(params)

      const { result, payment, payment_url } = response

      if (result === 'success') {
        dispatch(successErrorPaymentInfo({ payment, payment_url }))
      } else {
        dispatch(failureErrorPaymentInfo(response))
      }
    }
  } catch (err) {
    dispatch(failureErrorPaymentInfo(err.toString()))
  }
}

export const validatePersonalData = ({ params, onSuccess }) => async (
  dispatch,
  getState,
  { api },
) => {
  try {
    dispatch(requestPersonalDataValidation())
    const response = await api.validateCheckoutPersonalData(params)
    const { result, statusCode, ...data } = response

    if (result === 'success') {
      dispatch(successPersonalDataValidation({ params, nextStepData: data }))
      if (onSuccess) onSuccess()
    } else {
      const error = +statusCode === 500 ? { errors: { catched: data.toString() } } : data
      dispatch(failurePersonalDataValidation(error))
    }
  } catch (err) {
    dispatch(failurePersonalDataValidation({ errors: { catched: err.toString() } }))
  }
}

export const getShops = ({ onError, onSuccess } = {}) => async (
  dispatch,
  getState,
  { api },
) => {
  try {
    const response = await api.shopsNew()
    const { result, ...data } = response;
    if (result === 'success') {
      dispatch(successGetShops(data));
      onSuccess?.(response);
    } else {
      onError?.(response)
    }
  } catch (err) {
    console.error(err);
    onError?.(error)
  }
}

export const getTimeSlots = ({ params }) => async (
  dispatch,
  getState,
  { api },
) => {
  try {
    dispatch(initGetTimeslots())
    const response = await api.timeSlots(params)
    const { ...data } = response;

    if (data.result === 'success') {
      dispatch(successGetTimeslots(data))
    } else {
      dispatch(errorGetTimeslots(response))
    }

  } catch (err) {
    dispatch(errorGetTimeslots(err))
  }
}

export const validateDeliveryData = ({ params, onSuccess }) => async (
  dispatch,
  getState,
  { api },
) => {
  try {
    dispatch(requestDeliveryDataValidation())

    const response = await api.validateCheckoutDelivery(params)

    const { result, statusCode, ...data } = response

    if (result === 'success') {
      dispatch(successDeliveryDataValidation({ params, nextStepData: data }))
      if (onSuccess) onSuccess()
    } else {
      const error = +statusCode === 500 ? { errors: { catched: data.toString() } } : data
      dispatch(failureDeliveryDataValidation(error))
    }
  } catch (err) {
    dispatch(failureDeliveryDataValidation({ errors: { catched: err.toString() } }))
  }
}

export const validatePaymentData = ({ params, onSuccess, cleanOrders }) => async (
  dispatch,
  getState,
  { api },
) => {
  try {
    dispatch(requestPaymentDataValidation())
    const state = getState().checkout
    const allParams = {
      ...state.personalData.data,
      ...state.deliveryData.data,
      ...params,
    }

    if (allParams[PARAMS.PAYMENT_TYPE] === PARAMS.PAYMENT_TYPE_CERTIFICATE) {
      allParams[PARAMS.PAYMENT_TYPE] = PARAMS.PAYMENT_TYPE_CASH
    }

    let response = null

    if (params?.isNewCheckout) {
      delete params?.isNewCheckout
      response = await api.newCheckout({ ...allParams })
    } else {
      response = await api.checkout({ ...allParams })
    }

    const { result, statusCode, ...data } = response

    if (result === 'success') {
      dispatch(successPaymentDataValidation(params))
      dispatch(successOrderCreate({ ...data?.session, cleanOrders }))
      if (onSuccess) onSuccess({ redirectUrl: data?.redirect_url, order: data?.order, session: data?.session, redirectPageUrl: data?.redirect_page_url, })
    } else {
      const error = +statusCode === 500 ? { errors: { catched: data.toString() } } : data
      dispatch(failurePaymentDataValidation(error))
    }
  } catch (err) {
    dispatch(failurePaymentDataValidation({ errors: { catched: err.toString() } }))
  }
}


export const getLastOrderData = createSelector(
  state => state.checkout,
  checkout => ({ ...checkout.lastOrder }),
)

export const getRedirectPaymentData = createSelector(
  state => state.checkout,
  checkout => ({ ...checkout.redirectPaymentData }),
)
export const getErrorPaymentInfo = createSelector(
  state => state.checkout,
  checkout => ({ ...checkout.errorPaymentInfo }),
)

export const getPersonalData = createSelector(
  state => state.checkout,
  checkout => ({
    ...checkout.personalData,
    error: checkout.error,
  }),
)

export const getDeliveryData = createSelector(
  state => state.checkout,
  checkout => ({
    ...checkout.deliveryData,
    desiredDates: checkout.desiredDates,
    pickupPoints: checkout.pickupPoints,
    deliveryCosts: checkout.deliveryCosts,
    deliveryTypes: checkout.deliveryTypes,
    error: checkout.error,
  }),
)

export const getPaymentData = createSelector(
  state => state.checkout,
  checkout => ({
    ...checkout.paymentData,
    paymentTypes: checkout.paymentTypes,
    paymentTypeDescriptions: checkout.paymentTypeDescriptions,
    error: checkout.error,
  }),
)

export const timeSlotsSelector = createSelector(
  state => state.checkout,
  checkout => ({
    timeSlots: checkout.timeSlots,
  }),
)

export const deliveryServiceSelector = createSelector(
  state => state.checkout,
  checkout => ({
    service: checkout.service,
  }),
)

export const deliveryMaxItemsSelector = createSelector(
  state => state.checkout,
  checkout => ({
    maxItems: checkout.maxItems,
  }),
)

export const getNonDeliverableItems = createSelector(
  state => state.checkout,
  checkout => ({
    nonDeliverableItems: checkout.nonDeliverableItems,
  }),
)

export const getCheckoutError = createSelector(
  state => state.checkout,
  checkout => checkout.error,
)

export const {
  requestErrorPaymentInfo,
  successErrorPaymentInfo,
  failureErrorPaymentInfo,
  requestRedirectPaymentData,
  successRedirectPaymentData,
  failureRedirectPaymentData,
  requestPersonalDataValidation,
  successPersonalDataValidation,
  failurePersonalDataValidation,
  requestDeliveryDataValidation,
  successDeliveryDataValidation,
  failureDeliveryDataValidation,
  requestPaymentDataValidation,
  successPaymentDataValidation,
  failurePaymentDataValidation,
  successOrderCreate,
  successGetShops,
  initGetTimeslots,
  successGetTimeslots,
  errorGetTimeslots,
  resetCheckoutData,
  resetError,
  requestLastOrder,
  successLastOrder,
  failureLastOrder,
} = checkout.actions

export default checkout.reducer
